/**
 * Wall Tool - BIM 2025 Style Wall Creation
 * 
 * Handles wall sketching with parametric constraints:
 * - Location line options
 * - Height constraints (Unconnected, Up to Level)
 * - Base/Top offset
 * - Chain mode for continuous walls
 * - Wall joins
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from './BaseTool';
import { WallElement, WallGeometryData, Relation, Layer } from '../core/types';
import { familyManager } from '../core/FamilyManager';
import { WallJoinSolver } from '../core/WallJoinSolver';

export class WallTool extends BaseTool {
    id = 'wall';
    name = 'Wall';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Snap to grid
        const snappedPoint = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snappedPoint;
            this.currentPoint = snappedPoint;
        } else {
            // Finish segment
            this.createWallElement(this.startPoint, snappedPoint);

            if (this.context.bimStore.toolProperties.wallChain) {
                this.startPoint = snappedPoint;
                this.currentPoint = snappedPoint;
            } else {
                this.startPoint = null;
                this.currentPoint = null;
                this.updatePreview();
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            const snappedPoint = this.snapToGrid(point);
            this.currentPoint = snappedPoint;

            if (this.startPoint) {
                this.updatePreview();
            }
        }
    }

    onPointerUp(): void {
        // Click-click interaction, so nothing on up
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'wall',
                start: this.startPoint,
                end: this.currentPoint,
                width: 0.2 // Default or calculated width
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createWallElement(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const props = this.context.bimStore.toolProperties;
        const levels = this.context.bimStore.project.levels;

        // Default wall type if not specified (must match the registry name
        // in FamilyManager so getType()/Edit Type resolve the symbol).
        const wallType = 'Basic Wall: Generic - 200mm';

        const wall = createWall(start, end, {
            wallType,
            height: props.wallHeight,
            baseOffset: props.wallBaseOffset,
            topOffset: props.wallTopOffset,
            baseConstraint: props.wallBaseConstraint,
            topConstraint: props.wallTopConstraint,
            locationLine: props.wallLocationLine,
            chain: props.wallChain
        }, levels);

        if (!wall) return;

        // Process Joins
        const existingWalls = Array.from(this.context.bimStore.project.elements.values())
            .filter(e => e.category === 'Walls' && e.id !== wall.id) as WallElement[];

        const joins = processWallJoins(wall, existingWalls, this.context.bimStore.toolProperties.wallJoinType);
        wall.constraints.push(...joins);

        // Compute Profile for New Wall
        const connectedWalls = existingWalls.filter(w =>
            joins.some(j => j.targetId === w.id)
        );

        wall.geometryData.layerProfiles = WallJoinSolver.computeLayerProfiles(wall, connectedWalls);
        // Fallback for selection/legacy
        if (wall.geometryData.layerProfiles.length > 0) {
            // Use largest layer (usually core or total) as selection profile
            // For now, just use the first layer
            wall.geometryData.profile = wall.geometryData.layerProfiles[0];
        }

        // Update Connected Walls Profiles
        connectedWalls.forEach(connectedWall => {
            // Find walls connected to THIS connected wall (including the new one)
            const itsConnections = existingWalls
                .filter(w => w.id !== connectedWall.id)
                .concat([wall]) // Add new wall
                .filter(w => {
                    // Simple distance check or use existing constraints
                    const start = new THREE.Vector3(...connectedWall.geometryData.start);
                    const end = new THREE.Vector3(...connectedWall.geometryData.end);
                    const wStart = new THREE.Vector3(...w.geometryData.start);
                    const wEnd = new THREE.Vector3(...w.geometryData.end);
                    return start.distanceTo(wStart) < 0.01 || start.distanceTo(wEnd) < 0.01 ||
                        end.distanceTo(wStart) < 0.01 || end.distanceTo(wEnd) < 0.01;
                });

            const newLayerProfiles = WallJoinSolver.computeLayerProfiles(connectedWall, itsConnections);

            // We need to update the store for existing walls
            if (this.context && this.context.bimStore) {
                this.context.bimStore.updateElement(connectedWall.id, {
                    geometryData: {
                        ...connectedWall.geometryData,
                        layerProfiles: newLayerProfiles,
                        profile: newLayerProfiles.length > 0 ? newLayerProfiles[0] : undefined
                    }
                });
            }
        });

        this.context.bimStore.addElement(wall);
    }
}

export interface WallCreationOptions {
    wallType: string;
    height?: number;
    baseOffset?: number;
    topOffset?: number;
    baseConstraint?: string;
    topConstraint?: string;
    locationLine?: string;
    chain?: boolean;
    /** BIM Structure > Wall: mark as load-bearing (structural) bearing wall. */
    structural?: boolean;
}

export function createWall(
    start: THREE.Vector3,
    end: THREE.Vector3,
    options: WallCreationOptions,
    levels: Map<string, any>
): WallElement | null {
    const length = start.distanceTo(end);
    if (length < 0.01) return null; // Too short

    // Parse Family and Type from "Family: Type" string
    const [familyName, typeName] = options.wallType.split(': ').map(s => s.trim());

    let layers: Layer[] = [];
    const typeSymbol = familyManager.getType(familyName, typeName);

    if (typeSymbol && typeSymbol.parameters.layers) {
        layers = typeSymbol.parameters.layers;
    } else {
        // Fallback or default
        layers = [{ function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false }];
    }

    const thickness = layers.reduce((sum, layer) => sum + layer.thickness, 0);

    // Calculate Height
    let heightM = (options.height || 3000) / 1000;
    const baseOffset = options.baseOffset || 0;
    const topOffset = options.topOffset || 0;
    const baseConstraint = options.baseConstraint;
    const topConstraint = options.topConstraint || 'Unconnected';

    if (topConstraint !== 'Unconnected' && baseConstraint) {
        const baseLevel = levels.get(baseConstraint);
        const topLevel = levels.get(topConstraint);

        if (baseLevel && topLevel) {
            const h = (topLevel.elevation + topOffset) - (baseLevel.elevation + baseOffset);
            heightM = h / 1000;
        }
    }

    const geometryData: WallGeometryData = {
        start: [start.x, start.y, start.z],
        end: [end.x, end.y, end.z],
        height: heightM,
        thickness: thickness,
        layers: layers,
        baseOffset: baseOffset / 1000,
        baseConstraintId: baseConstraint || '',
        topConstraintId: topConstraint,
        topOffset: topOffset / 1000,
        locationLine: (options.locationLine as any) || 'WallCenterline'
    };

    const isStructural = options.structural === true;
    const wall: WallElement = {
        id: uuidv4(),
        category: 'Walls',
        type: options.wallType,
        parameters: {
            'Length': `${length.toFixed(2)} m`,
            'Unconnected Height': `${heightM.toFixed(2)} m`,
            'Base Constraint': baseConstraint,
            'Top Constraint': topConstraint,
            'Base Offset': `${baseOffset} mm`,
            'Top Offset': `${topOffset} mm`,
            'Location Line': options.locationLine,
            'Structural': isStructural,
            'Room Bounding': !isStructural,
            'Width': `${thickness.toFixed(3)} m`
        },
        constraints: [],
        geometryData
    };

    return wall;
}

/**
 * Find and process wall joins
 */
export function processWallJoins(
    newWall: WallElement,
    existingWalls: WallElement[],
    defaultJoinType: 'Miter' | 'Butt' | 'SquareOff' = 'Miter'
): Relation[] {
    const joins: Relation[] = [];
    const newStart = new THREE.Vector3(...newWall.geometryData.start);
    const newEnd = new THREE.Vector3(...newWall.geometryData.end);
    const tolerance = 0.01; // 10mm tolerance

    existingWalls.forEach(wall => {
        const wallStart = new THREE.Vector3(...wall.geometryData.start);
        const wallEnd = new THREE.Vector3(...wall.geometryData.end);

        // Check for endpoint connections
        if (newStart.distanceTo(wallStart) < tolerance ||
            newStart.distanceTo(wallEnd) < tolerance ||
            newEnd.distanceTo(wallStart) < tolerance ||
            newEnd.distanceTo(wallEnd) < tolerance) {
            joins.push({
                type: 'JOIN',
                targetId: wall.id,
                data: { joinType: defaultJoinType }
            });
            return;
        }

        // T-junction: my endpoint touches the other wall's body (and the
        // symmetric case). WallJoinSolver.applyJoinsAtEnd's T-join path only
        // fires for walls it receives, so the relation must exist up front.
        const touchesBody = (pt: THREE.Vector3): boolean => {
            const vec = wallEnd.clone().sub(wallStart);
            const len = vec.length();
            if (len < 0.001) return false;
            const dir = vec.clone().normalize();
            const toPoint = pt.clone().sub(wallStart);
            const dot = toPoint.dot(dir);
            if (dot <= 0.01 || dot >= len - 0.01) return false;
            const proj = wallStart.clone().add(dir.multiplyScalar(dot));
            return proj.distanceTo(pt) < tolerance;
        };

        if (touchesBody(newStart) || touchesBody(newEnd)) {
            joins.push({
                type: 'JOIN',
                targetId: wall.id,
                data: { joinType: defaultJoinType, tJoin: true }
            });
        }
    });

    return joins;
}
